import {IUploadInPartsOptions} from '@toolify/client/src/modules/files/upload/types/IUploadInPartsOptions'
import {IUploadSessionPart} from '@toolify/client/src/modules/files/upload/types/IUploadSessionPart'
import {IUploadInPartsResult} from '@toolify/client/src/modules/files/upload/types/IUploadInPartsResult'
import {formatBytes, formatThroughput} from '@toolify/client/src/modules/files/upload/uploadDebugLog'

// logging every part would flood the console on a multi-thousand-part upload
const MAX_INDIVIDUALLY_LOGGED_PARTS = 24
const PART_CONCURRENCY = 4
const PART_RETRY_DELAYS_MS = [0, 1000, 3000, 5000]
const MAX_PART_NUMBERS_PER_SIGN_REQUEST = 25
const EXPIRED_PART_URL_STATUS = 403

export class UploadPartError extends Error {
  public readonly partNumber: number
  public readonly status: number | null
  public readonly responseText: string

  constructor(message: string, partNumber: number, status: number | null, responseText = '') {
    super(message)
    Object.setPrototypeOf(this, new.target.prototype)
    this.name = 'UploadPartError'
    this.partNumber = partNumber
    this.status = status
    this.responseText = responseText
  }
}

export function createUploadAbortError(): Error {
  const error = new Error('Upload aborted')
  error.name = 'AbortError'
  return error
}

function isUploadAbortError(error: unknown): boolean {
  return (error as Error)?.name === 'AbortError'
}

function getUploadPartErrorStatus(error: unknown): number | null {
  return error instanceof UploadPartError ? error.status : null
}

function isRetryableUploadPartError(error: unknown): boolean {
  const status = getUploadPartErrorStatus(error)
  if(status === null) {
    return true
  }
  return status >= 500
}

function delay(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> = null
    const cleanup = () => {
      clearTimeout(timeout)
      signal?.removeEventListener('abort', onAbort)
    }
    const onAbort = () => {
      cleanup()
      reject(createUploadAbortError())
    }
    if(signal?.aborted) {
      reject(createUploadAbortError())
      return
    }
    signal?.addEventListener('abort', onAbort)
    timeout = setTimeout(() => {
      cleanup()
      resolve()
    }, delayMs)
  })
}

export async function uploadInParts(options: IUploadInPartsOptions): Promise<IUploadInPartsResult> {
  const {
    file,
    session,
    signMoreParts,
    onProgress,
    signal,
    debugSession,
    pauseController,
    alreadyUploadedPartNumbers,
  } = options
  const {partSize, partCount} = session
  const totalBytes = file.size
  const startedAtInMs = performance.now()
  let retryCount = 0
  let reSignCount = 0
  let signRequestCount = 0
  let peakConcurrency = 0
  let lastLoggedProgressStep = 0
  debugSession?.log('parts: plan', {
    partCount,
    partSize: formatBytes(partSize),
    totalBytes: formatBytes(totalBytes),
    concurrency: Math.min(PART_CONCURRENCY, partCount),
    presignedUpfront: (session.parts || []).length,
  })
  if(partSize <= 0 || partCount <= 0) {
    throw new Error('Upload session has an empty part plan')
  }
  if(Math.ceil(totalBytes / partSize) !== partCount) {
    throw new Error('Upload session part plan does not match the file size')
  }
  if(signal?.aborted) {
    throw createUploadAbortError()
  }

  const getPartBytes = (partNumber: number): number => {
    return Math.min(partNumber * partSize, totalBytes) - (partNumber - 1) * partSize
  }

  const urlByPartNumber = new Map<number, string>()
  const loadedBytesByPartNumber = new Map<number, number>()
  const activeRequests = new Set<XMLHttpRequest>()
  // parts the storage already holds from an earlier, interrupted run of this same session
  const skippedPartNumbers = new Set(
    (alreadyUploadedPartNumbers || []).filter(partNumber => {
      return partNumber >= 1 && partNumber <= partCount
    }),
  )
  let confirmedBytes = 0
  // bytes this run actually put on the wire, which is what a throughput figure may be derived from -
  // confirmedBytes also contains the parts a resume skipped, and dividing those by this run's duration lies
  let transferredBytes = 0
  for(const partNumber of skippedPartNumbers) {
    confirmedBytes += getPartBytes(partNumber)
  }
  if(skippedPartNumbers.size) {
    debugSession?.log('parts: resuming', {
      alreadyUploaded: skippedPartNumbers.size,
      of: partCount,
      skippedBytes: formatBytes(confirmedBytes),
    })
  }
  let lastReportedBytes = 0
  let isStopped = false
  let firstError: unknown = null
  let nextPartNumber = 1
  let signPartsPromise: Promise<void> = null

  const applySignedParts = (parts: IUploadSessionPart[]) => {
    for(const part of parts) {
      urlByPartNumber.set(part.partNumber, part.url)
    }
  }

  applySignedParts(session.parts || [])

  const reportProgress = () => {
    if(!onProgress) {
      return
    }
    let loadedBytes = confirmedBytes
    for(const partLoadedBytes of loadedBytesByPartNumber.values()) {
      loadedBytes += partLoadedBytes
    }
    loadedBytes = Math.min(loadedBytes, totalBytes)
    if(loadedBytes <= lastReportedBytes) {
      return
    }
    const deltaBytes = loadedBytes - lastReportedBytes
    lastReportedBytes = loadedBytes
    if(partCount > MAX_INDIVIDUALLY_LOGGED_PARTS) {
      const progressStep = Math.floor((loadedBytes / totalBytes) * 10)
      if(progressStep > lastLoggedProgressStep) {
        lastLoggedProgressStep = progressStep
        const elapsedInMs = performance.now() - startedAtInMs
        debugSession?.log('parts: progress', {
          percent: `${progressStep * 10}%`,
          transferred: formatBytes(loadedBytes),
          throughput: formatThroughput(loadedBytes, elapsedInMs),
        })
      }
    }
    onProgress({
      loaded: loadedBytes,
      total: totalBytes,
      lengthComputable: true,
      bytes: deltaBytes,
    })
  }

  const stopActiveRequests = () => {
    isStopped = true
    const requests = [...activeRequests]
    activeRequests.clear()
    for(const request of requests) {
      request.abort()
    }
    // workers parked on the pause gate hold no request to abort, so they have to be woken explicitly -
    // otherwise a part failing while the transfer is paused leaves them waiting forever and the upload never settles
    pauseController?.resume()
  }

  const onExternalAbort = () => {
    stopActiveRequests()
  }

  const signPartUrlsFrom = async (partNumber: number): Promise<void> => {
    const partNumbers: number[] = []
    for(
      let candidatePartNumber = partNumber;
      candidatePartNumber <= partCount && partNumbers.length < MAX_PART_NUMBERS_PER_SIGN_REQUEST;
      candidatePartNumber++
    ) {
      if(!urlByPartNumber.has(candidatePartNumber) && !skippedPartNumbers.has(candidatePartNumber)) {
        partNumbers.push(candidatePartNumber)
      }
    }
    if(!partNumbers.length) {
      return
    }
    signRequestCount++
    const signStartedAtInMs = performance.now()
    applySignedParts(await signMoreParts(partNumbers))
    debugSession?.log('parts: signed more urls', {
      count: partNumbers.length,
      from: partNumbers[0],
      durationInMs: Math.round(performance.now() - signStartedAtInMs),
    })
  }

  const getPartUrl = async (partNumber: number): Promise<string> => {
    while(!urlByPartNumber.has(partNumber)) {
      if(signPartsPromise) {
        await signPartsPromise
        continue
      }
      signPartsPromise = signPartUrlsFrom(partNumber).finally(() => {
        signPartsPromise = null
      })
      await signPartsPromise
      if(!urlByPartNumber.has(partNumber)) {
        throw new Error(`Upload session did not return an url for part ${partNumber}`)
      }
    }
    return urlByPartNumber.get(partNumber)
  }

  const reSignPartUrl = async (partNumber: number): Promise<void> => {
    urlByPartNumber.delete(partNumber)
    applySignedParts(await signMoreParts([partNumber]))
    if(!urlByPartNumber.has(partNumber)) {
      throw new Error(`Upload session did not return an url for part ${partNumber}`)
    }
  }

  const sendPart = (partNumber: number, url: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const startByte = (partNumber - 1) * partSize
      const endByte = Math.min(startByte + partSize, totalBytes)
      const request = new XMLHttpRequest()
      request.open('PUT', url, true)
      request.upload.onprogress = (event) => {
        loadedBytesByPartNumber.set(partNumber, event.loaded)
        reportProgress()
      }
      request.onload = () => {
        activeRequests.delete(request)
        if(request.status >= 200 && request.status < 300) {
          loadedBytesByPartNumber.delete(partNumber)
          confirmedBytes += endByte - startByte
          transferredBytes += endByte - startByte
          reportProgress()
          resolve()
          return
        }
        reject(new UploadPartError(
          `Part ${partNumber} upload failed with status ${request.status}`,
          partNumber,
          request.status,
          request.responseText,
        ))
      }
      request.onerror = () => {
        activeRequests.delete(request)
        reject(new UploadPartError(`Part ${partNumber} upload failed`, partNumber, null))
      }
      request.ontimeout = () => {
        activeRequests.delete(request)
        reject(new UploadPartError(`Part ${partNumber} upload timed out`, partNumber, null))
      }
      request.onabort = () => {
        activeRequests.delete(request)
        reject(createUploadAbortError())
      }
      activeRequests.add(request)
      peakConcurrency = Math.max(peakConcurrency, activeRequests.size)
      // a slice of the disk-backed File is sent as-is so the bytes never enter the js heap;
      // it also carries no content type, which keeps the part request free of signed headers
      request.send(file.slice(startByte, endByte))
    })
  }

  const uploadPart = async (partNumber: number): Promise<void> => {
    let retryIndex = 0
    let hasReSignedPartUrl = false
    const partStartedAtInMs = performance.now()
    for(;;) {
      if(isStopped || signal?.aborted) {
        throw createUploadAbortError()
      }
      try {
        const url = await getPartUrl(partNumber)
        await sendPart(partNumber, url)
        if(partCount <= MAX_INDIVIDUALLY_LOGGED_PARTS) {
          const partBytes = Math.min(partNumber * partSize, totalBytes) - (partNumber - 1) * partSize
          const partDurationInMs = Math.round(performance.now() - partStartedAtInMs)
          debugSession?.log(`part ${partNumber}/${partCount}: ok`, {
            bytes: formatBytes(partBytes),
            durationInMs: partDurationInMs,
            throughput: formatThroughput(partBytes, partDurationInMs),
            attempts: retryIndex + 1,
          })
        }
        return
      } catch(error) {
        loadedBytesByPartNumber.set(partNumber, 0)
        if(isUploadAbortError(error)) {
          throw error
        }
        if(getUploadPartErrorStatus(error) === EXPIRED_PART_URL_STATUS && !hasReSignedPartUrl) {
          hasReSignedPartUrl = true
          reSignCount++
          debugSession?.warn(`part ${partNumber}: presigned url rejected with 403, re-signing`)
          await reSignPartUrl(partNumber)
          continue
        }
        retryIndex++
        if(!isRetryableUploadPartError(error) || retryIndex >= PART_RETRY_DELAYS_MS.length) {
          debugSession?.warn(`part ${partNumber}: giving up`, {
            status: getUploadPartErrorStatus(error),
            attempts: retryIndex,
            message: (error as Error)?.message,
            responseText: (error as UploadPartError)?.responseText?.slice(0, 300),
          })
          throw error
        }
        retryCount++
        debugSession?.warn(`part ${partNumber}: retrying`, {
          status: getUploadPartErrorStatus(error),
          attempt: retryIndex,
          delayInMs: PART_RETRY_DELAYS_MS[retryIndex],
        })
        await delay(PART_RETRY_DELAYS_MS[retryIndex], signal)
      }
    }
  }

  const runWorker = async (): Promise<void> => {
    for(;;) {
      if(isStopped || firstError) {
        return
      }
      // pausing gates the START of the next part, so parts already in flight still finish
      if(pauseController?.isPaused) {
        debugSession?.log('parts: paused', {
          confirmed: formatBytes(confirmedBytes),
          of: formatBytes(totalBytes),
        })
        await pauseController.waitWhilePaused(signal)
        debugSession?.log('parts: resumed')
        if(isStopped || firstError) {
          return
        }
      }
      const partNumber = nextPartNumber++
      if(partNumber > partCount) {
        return
      }
      if(skippedPartNumbers.has(partNumber)) {
        continue
      }
      try {
        await uploadPart(partNumber)
      } catch(error) {
        if(!firstError) {
          firstError = error
        }
        stopActiveRequests()
        return
      }
    }
  }

  signal?.addEventListener('abort', onExternalAbort)
  try {
    const workerCount = Math.min(PART_CONCURRENCY, partCount)
    await Promise.all(Array.from({length: workerCount}, () => {
      return runWorker()
    }))
  } finally {
    signal?.removeEventListener('abort', onExternalAbort)
  }

  if(signal?.aborted) {
    debugSession?.log('parts: aborted', {
      transferred: formatBytes(confirmedBytes),
      of: formatBytes(totalBytes),
    })
    throw createUploadAbortError()
  }
  if(firstError) {
    debugSession?.warn('parts: failed', {
      transferred: formatBytes(confirmedBytes),
      of: formatBytes(totalBytes),
      message: (firstError as Error)?.message,
    })
    throw firstError
  }
  reportProgress()
  const durationInMs = Math.round(performance.now() - startedAtInMs)
  if(confirmedBytes !== totalBytes) {
    debugSession?.warn('parts: confirmed byte count does not match the file size', {
      confirmed: formatBytes(confirmedBytes),
      expected: formatBytes(totalBytes),
    })
  }
  debugSession?.log('parts: done', {
    partCount,
    partsSent: partCount - skippedPartNumbers.size,
    bytesSent: formatBytes(transferredBytes),
    ofTotal: formatBytes(confirmedBytes),
    durationInMs,
    throughput: formatThroughput(transferredBytes, durationInMs),
    peakConcurrency,
    retries: retryCount,
    reSigns: reSignCount,
    signRequests: signRequestCount,
  })
  return {transferredBytes}
}
